import { useEffect, useState } from "react";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect } from "primereact/multiselect";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "primereact/tooltip";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { categoryEstimationConfig, getKaQuestionTypeLabel } from "sj-ui-toolkit/src/atlas/utils";
import { languageDisplayNames } from "sj-ui-toolkit/src/constants/content";
import {
  ASSIGN_SPECIFIC,
  SELF_SELECT,
  SECURE_CODING,
  CATEGORY_VARIANTS,
  FADED_KA_CARD_IMAGES,
  KA_CARD_IMAGE_MAP,
} from "@/constants/knowledgeAssessments";
import {
  hasSelectedLanguages,
  sortByCustomOrder,
  convertDurationToMinutes,
  type CategoryItem,
} from "@/helpers/knowledgeAssessments";
import { ContentCard } from "./ContentCard";
import "sj-ui-toolkit/src/atlas/styles/backgrounds.scss";

// As many columns as fit at 250px+, capped at 4 with spacing (1.5rem matches gap-6)
const CARD_GRID_COLUMNS =
  "grid-cols-[repeat(auto-fit,minmax(max(min(250px,100%),calc((100%_-_3_*_1.5rem)/4)),1fr))]";

interface KnowledgeAssessmentContent {
  examKey?: string;
  status?: { progress: string };
  filters: { sections: string[] };
  parameters: { allowedProgrammingLanguages: string[] };
  timeLimit?: string;
}

export interface OriginalValues {
  parameters: { allowedProgrammingLanguages: string[] };
}

interface AssessmentFormContentProps {
  values: KnowledgeAssessmentContent;
  categories: CategoryItem[];
  handleChange: (values: KnowledgeAssessmentContent) => void;
  alreadyStarted: boolean;
  showValidationMessages: boolean;
  validationErrors: { hasError: boolean; message: string };
  originalValues: OriginalValues | null;
}

export default function AssessmentFormContent({
  values,
  categories,
  handleChange,
  alreadyStarted,
  showValidationMessages,
  validationErrors,
  originalValues,
}: AssessmentFormContentProps) {
  const [timedAssignment, setTimedAssignment] = useState(!!values.timeLimit);
  const [selectedRadio, setSelectedRadio] = useState(() =>
    hasSelectedLanguages(values) ? ASSIGN_SPECIFIC : SELF_SELECT
  );
  const [checkedCategories, setCheckedCategories] = useState<string[]>([]);

  useEffect(() => {
    setCheckedCategories(values.filters.sections);
  }, [values]);

  const sortedCategories = sortByCustomOrder(categories);

  const secureCodingCategory = categories.find(c => c.path === SECURE_CODING);
  const secureCodingLanguages = secureCodingCategory?.programmingLanguages ?? [];
  const languageOptions = secureCodingLanguages.map(lang => ({
    label: languageDisplayNames[lang] ?? lang,
    value: lang,
  }));

  const originalLanguages = originalValues?.parameters.allowedProgrammingLanguages ?? [];
  const cannotSwitchToSpecific = alreadyStarted && originalLanguages.length === 0;
  const cannotRemoveOriginalLanguages = alreadyStarted && originalLanguages.length > 0;
  const isLanguageFieldDisabled = !checkedCategories.includes(SECURE_CODING);

  const updateContentValues = (updatedCategories: string[], radioValue: string) => {
    const isAssignSpecific = radioValue === ASSIGN_SPECIFIC;
    const existingLanguages = values.parameters.allowedProgrammingLanguages;
    handleChange({
      ...values,
      filters: { ...values.filters, sections: updatedCategories },
      parameters: {
        ...values.parameters,
        allowedProgrammingLanguages: isAssignSpecific ? existingLanguages : [],
      },
    });
  };

  const onCategoryToggle = (category: CategoryItem) => {
    const isSelected = checkedCategories.includes(category.path);
    const updated = isSelected
      ? checkedCategories.filter(k => k !== category.path)
      : [...checkedCategories, category.path];
    setCheckedCategories(updated);
    updateContentValues(updated, selectedRadio);
  };

  const onLanguageSettingChange = (e: { value: string }) => {
    const newRadio = e.value;
    setSelectedRadio(newRadio);

    if (newRadio === ASSIGN_SPECIFIC && alreadyStarted && originalLanguages.length > 0) {
      handleChange({
        ...values,
        filters: { ...values.filters, sections: checkedCategories },
        parameters: { ...values.parameters, allowedProgrammingLanguages: originalLanguages },
      });
      return;
    }
    updateContentValues(checkedCategories, newRadio);
  };

  const onLanguageChange = (e: { value: string[] }) => {
    if (cannotRemoveOriginalLanguages) {
      const missing = originalLanguages.filter(lang => !e.value.includes(lang));
      if (missing.length > 0) {
        handleChange({
          ...values,
          parameters: {
            ...values.parameters,
            allowedProgrammingLanguages: [...new Set([...e.value, ...originalLanguages])],
          },
        });
        return;
      }
    }
    handleChange({
      ...values,
      parameters: { ...values.parameters, allowedProgrammingLanguages: e.value },
    });
  };

  const handleTimeLimitSetting = (e: { checked?: boolean }) => {
    setTimedAssignment(e.checked ?? false);
    if (e.checked) {
      if (!values.timeLimit) {
        handleChange({ ...values, timeLimit: "3600s" });
      }
    } else {
      const { timeLimit: _removed, ...rest } = values;
      handleChange(rest as KnowledgeAssessmentContent);
    }
  };

  const onTimeLimitChange = (e: { value?: number | null }) => {
    if (timedAssignment && e.value != null) {
      handleChange({ ...values, timeLimit: `${e.value * 60}s` });
    }
  };

  return (
    <>
      {/* Category grid */}
      <div className={`grid ${CARD_GRID_COLUMNS} gap-6 w-full`}>
        {sortedCategories.map((category, index) => {
          const variant = CATEGORY_VARIANTS[index % CATEGORY_VARIANTS.length] ?? "mauve";
          const estFinishTime =
            (categoryEstimationConfig[category.path]?.estFinishTime ?? "10 - 15") + " minutes";
          const isSelected = checkedCategories.includes(category.path);
          return (
            <ContentCard
              key={category.path}
              image={KA_CARD_IMAGE_MAP[category.path]}
              imageAlt={category.title ?? ""}
              imageClassName={FADED_KA_CARD_IMAGES.has(category.path) ? "opacity-30" : undefined}
              title={category.title ?? ""}
              description={
                <>
                  <p className="body-sm font-semibold mb-2">
                    {getKaQuestionTypeLabel(category.path)}
                  </p>
                  <p className="body-sm mb-3">
                    <span className="font-semibold">Estimate: </span>
                    {estFinishTime}
                  </p>
                  <p className="body-sm">{category.description ?? ""}</p>
                </>
              }
              buttonLabel={isSelected ? "Remove" : "Include"}
              onClick={() => onCategoryToggle(category)}
              selected={isSelected}
              disabled={alreadyStarted}
              variant={variant}
            />
          );
        })}
      </div>

      {/* Language selection */}
      <div className="mt-8">
        <div className={`mb-2 font-semibold ${isLanguageFieldDisabled ? "opacity-50" : ""}`}>
          Language Selection
          <Tooltip target="#language-selection-tooltip" position="right">
            Language selection only applies to Secure Coding assessment questions.
          </Tooltip>
          <i
            id="language-selection-tooltip"
            className="pi pi-question-circle ml-1 cursor-pointer"
          />
        </div>
        <fieldset disabled={isLanguageFieldDisabled} className="flex flex-col gap-3">
          <legend className="sr-only">Language selection</legend>
          <div className="flex items-center gap-2">
            <RadioButton
              inputId={SELF_SELECT}
              name="languageSetting"
              value={SELF_SELECT}
              onChange={onLanguageSettingChange}
              checked={selectedRadio === SELF_SELECT}
            />
            <label htmlFor={SELF_SELECT}>Allow learners to select any available language</label>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <RadioButton
                inputId={ASSIGN_SPECIFIC}
                name="languageSetting"
                value={ASSIGN_SPECIFIC}
                onChange={onLanguageSettingChange}
                checked={selectedRadio === ASSIGN_SPECIFIC}
                disabled={cannotSwitchToSpecific}
              />
              <label htmlFor={ASSIGN_SPECIFIC}>Limit language selection to the following:</label>
              {cannotSwitchToSpecific && (
                <>
                  <Tooltip target="#limit-language-tooltip" position="right">
                    Cannot change from &quot;all languages&quot; to &quot;specific languages&quot;
                    once an assessment has started.
                  </Tooltip>
                  <i
                    id="limit-language-tooltip"
                    className="pi pi-question-circle cursor-pointer text-secondary"
                  />
                </>
              )}
            </div>
            <MultiSelect
              value={values.parameters.allowedProgrammingLanguages}
              onChange={onLanguageChange}
              options={languageOptions}
              display="chip"
              disabled={isLanguageFieldDisabled || selectedRadio !== ASSIGN_SPECIFIC}
              placeholder="Select language(s)"
              className="w-full max-w-md"
            />
            {cannotRemoveOriginalLanguages &&
              selectedRadio === ASSIGN_SPECIFIC &&
              originalLanguages.length > 0 && (
                <p className="body-sm text-secondary">
                  Note: Original languages (
                  {originalLanguages.map(l => languageDisplayNames[l] ?? l).join(", ")}) cannot be
                  removed from this assessment.
                </p>
              )}
          </div>
        </fieldset>
      </div>

      {/* Timed assignment */}
      <fieldset disabled={alreadyStarted} className="flex items-center gap-4 mt-6">
        <legend className="sr-only">Assessment time settings</legend>
        <Checkbox
          inputId="timed-assessment"
          onChange={handleTimeLimitSetting}
          checked={timedAssignment}
        />
        <label htmlFor="timed-assessment">Timed assessment</label>
        <InputNumber
          inputId="timed-assessment-minutes"
          value={
            timedAssignment && values.timeLimit ? convertDurationToMinutes(values.timeLimit) : null
          }
          onValueChange={onTimeLimitChange}
          min={1}
          suffix=" minutes"
          placeholder="0 minutes"
          showButtons
          buttonLayout="horizontal"
          step={1}
          incrementButtonIcon="pi pi-plus"
          decrementButtonIcon="pi pi-minus"
          disabled={!timedAssignment}
        />
      </fieldset>

      {showValidationMessages && validationErrors.hasError && (
        <div className="mt-4">
          <ValidationMessage severity="error" text={validationErrors.message} />
        </div>
      )}
    </>
  );
}
